import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { Box } from '@mui/material';
import {
  AIFileUploadV2ContextProvider,
  useAIFileUploadV2Context,
} from 'src/contexts/AIFileUploadV2Context';
import FileDetail from 'src/components/NewRenterInterface/AIFileUploadV2/FileDetail';
import InsightsReview from 'src/components/NewRenterInterface/AIFileUploadV2/InsightsReview';
import FileUploadInput from 'src/pages/Interview/components/inputs/FileUploadInput';
import { useInterviewOverlay } from 'src/pages/Interview/InterviewOverlayContext';
import type {
  ExistingSurveyFile,
  InterviewState,
} from 'src/redux/slices/Interview/types';
import {
  deleteSurveyFile,
  getSurveyFileFromRenterFile,
} from 'src/services/interviewSurveyFiles';
import SurveyFileDetailView from 'src/pages/Interview/components/SurveyFileDetailView';
import { mergeSurveyFiles } from 'src/pages/Interview/components/inputs/mergeSurveyFiles';

interface Props {
  surveyId: number;
  certificationId?: number;
  qitId: number;
  answerGroupId?: number | null;
  questionGroupId?: number;
  maxFiles?: number;
  /** Called when Apply-All completes; passes the current file list. */
  onComplete: (fileList?: ExistingSurveyFile[]) => void;
  /** Called when upload state changes; passes the total number of uploaded files. */
  onHasUploadsChange?: (count: number) => void;
  /** Called when a new answer group is created via lazy creation during file upload. */
  onAnswerGroupCreated?: (groupId: number) => void;
  onReadyStateChange?: (state: { processing: boolean; ready: boolean }) => void;
  onContinueTrigger?: (fn: (() => void) | null) => void;
  /** Skip the initial RenterFile fetch (brand-new multi-group instance). */
  skipInitialFileFetch?: boolean;
  existingSurveyFiles?: ExistingSurveyFile[];
}

/**
 * Embeds the renter_hub doc-hub flow inline in the interview File question,
 * anchored to a specific file QIT via targetQitId. Route-based step
 * transitions in the standalone doc-hub are replaced by local step state.
 */
export default function InterviewDocHub({
  surveyId,
  certificationId,
  qitId,
  answerGroupId,
  questionGroupId,
  maxFiles,
  onComplete,
  onHasUploadsChange,
  onAnswerGroupCreated,
  onReadyStateChange,
  onContinueTrigger,
  skipInitialFileFetch,
  existingSurveyFiles,
}: Props): JSX.Element {
  return (
    <AIFileUploadV2ContextProvider
      surveyId={surveyId}
      certificationId={certificationId}
      targetQitId={qitId}
      targetAnswerGroupId={answerGroupId ?? undefined}
      skipInitialFileFetch={skipInitialFileFetch}
    >
      <InterviewDocHubInner
        onComplete={onComplete}
        onHasUploadsChange={onHasUploadsChange}
        questionGroupId={questionGroupId}
        maxFiles={maxFiles}
        onAnswerGroupCreated={onAnswerGroupCreated}
        onReadyStateChange={onReadyStateChange}
        onContinueTrigger={onContinueTrigger}
        existingSurveyFiles={existingSurveyFiles}
        surveyId={surveyId}
      />
    </AIFileUploadV2ContextProvider>
  );
}

function InterviewDocHubInner({
  onComplete,
  onHasUploadsChange,
  questionGroupId,
  maxFiles,
  onAnswerGroupCreated,
  onReadyStateChange,
  onContinueTrigger,
  existingSurveyFiles,
  surveyId,
}: {
  onComplete: (fileList?: ExistingSurveyFile[]) => void;
  onHasUploadsChange?: (count: number) => void;
  questionGroupId?: number;
  maxFiles?: number;
  onAnswerGroupCreated?: (groupId: number) => void;
  onReadyStateChange?: (state: { processing: boolean; ready: boolean }) => void;
  onContinueTrigger?: (fn: (() => void) | null) => void;
  existingSurveyFiles?: ExistingSurveyFile[];
  surveyId: number;
}): JSX.Element {
  const {
    files: { removeFile, fileMap, fileIds },
  } = useAIFileUploadV2Context();
  const { setBackOverride, contentRef } = useInterviewOverlay();
  const [step, setStep] = useState<'upload' | 'detail' | 'review'>('upload');
  const [detailFileId, setDetailFileId] = useState<number | null>(null);
  const [deletedSurveyFileIds, setDeletedSurveyFileIds] = useState<Set<number>>(
    new Set()
  );
  const [surveyFileDetail, setSurveyFileDetail] =
    useState<ExistingSurveyFile | null>(null);
  // ENG-4064: documents the backend linked onto this question. Held here (not
  // in FileUploadInput) so the durable answer saved on Continue, the counters
  // and the tiles all read one merged list.
  const [linkedSurveyFiles, setLinkedSurveyFiles] = useState<
    ExistingSurveyFile[]
  >([]);

  // Lock the underlying content scroll while an overlay (file detail / review)
  // is open, so the question's Back/Skip/Continue buttons can't be scrolled
  // into view behind the overlay.
  const overlayOpen =
    step === 'detail' || step === 'review' || surveyFileDetail !== null;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    if (overlayOpen) {
      el.style.overflow = 'hidden';
    } else {
      el.style.overflow = '';
    }
    return () => {
      el.style.overflow = '';
    };
  }, [overlayOpen, contentRef]);

  // Read can_answer from the IPE response. False means the acting user lacks
  // proxy authority for this survey — the upload zone should be hidden.
  // Defaults to true (IPE not yet loaded or self-mode) so behaviour is
  // unchanged until the interview initialises. See ENG-3002.
  const canAnswer = useSelector(
    (state: { interview: InterviewState }) => state.interview.canAnswer,
    shallowEqual
  );

  const visibleSurveyFiles = mergeSurveyFiles(
    existingSurveyFiles,
    linkedSurveyFiles
  ).filter((sf) => !deletedSurveyFileIds.has(sf.file_id));

  const handleSurveyFilesLinked = useCallback(
    (files: ExistingSurveyFile[]): void => {
      // The step snapshot wins on collision (see mergeSurveyFiles), so a
      // reload that already knows the file replaces the local copy.
      setLinkedSurveyFiles((prev) => mergeSurveyFiles(prev, files));
    },
    []
  );

  // Collect the full file list (pre-existing + newly uploaded from context)
  // so the callback can store them for the reviewing branch.
  const fileMapRef = useRef(fileMap);
  fileMapRef.current = fileMap;
  const fileIdsRef = useRef(fileIds);
  fileIdsRef.current = fileIds;
  const visibleSurveyFilesRef = useRef(visibleSurveyFiles);
  visibleSurveyFilesRef.current = visibleSurveyFiles;
  // Ref so handleCompleteWithFiles can call goToUpload without a forward-ref
  // ordering issue (goToUpload is declared below with useCallback).
  const goToUploadRef = useRef<() => void>(() => undefined);

  const handleCompleteWithFiles = useCallback(async (): Promise<void> => {
    const ids = fileIdsRef.current;
    const existing = visibleSurveyFilesRef.current;

    // Fetch real SurveyFile data for each RenterFile ID from the v2 endpoint.
    // This bridges RenterFile placeholder IDs to actual SurveyFile metadata
    // (pdf_file_url, real file_id, real status) created by Apply All.
    const resolvedFiles: ExistingSurveyFile[] = [];
    for (const id of ids) {
      try {
        const sf = await getSurveyFileFromRenterFile(id);
        resolvedFiles.push(sf);
      } catch {
        // If the bridge fails (e.g. Apply All hasn't completed yet), fall back
        // to a placeholder so the file is at least visible in the list.
        const f = fileMapRef.current[id];
        resolvedFiles.push({
          file_id: id,
          file_name: f?.original_filename ?? '',
          pdf_file_url: '',
          survey_answer_id: 0,
          status: 1,
          created_at: new Date().toISOString(),
        });
      }
    }
    const allFiles = [...existing, ...resolvedFiles];
    onComplete(allFiles);
    // Reset step to 'upload' so that if the interview advances to another file
    // question in the same group (updateVisibleQueue path), the reused
    // InterviewDocHub instance does not keep step='review' — which would leave
    // the InsightsReview portal open and show "No insights" for the new QIT.
    goToUploadRef.current();
  }, [onComplete]);

  const goToUpload = useCallback(() => {
    setDetailFileId(null);
    setStep('upload');
    setBackOverride(null);
  }, [setBackOverride]);
  goToUploadRef.current = goToUpload;

  const goToReview = useCallback(() => {
    setStep('review');
    setBackOverride(goToUpload);
  }, [setBackOverride, goToUpload]);

  const goToDetail = useCallback(
    (fileId: number) => {
      setDetailFileId(fileId);
      setStep('detail');
      setBackOverride(goToUpload);
    },
    [setBackOverride, goToUpload]
  );

  useEffect(() => {
    return () => setBackOverride(null);
  }, [setBackOverride]);

  useEffect(() => {
    onContinueTrigger?.(goToReview);
    return () => onContinueTrigger?.(null);
  }, [onContinueTrigger, goToReview]);

  const handleRemoveFromDetail = useCallback(
    async (fileId: number) => {
      await removeFile(fileId);
      goToUpload();
    },
    [removeFile, goToUpload]
  );

  const handleSurveyFileDelete = useCallback(
    async (sf: ExistingSurveyFile): Promise<void> => {
      await deleteSurveyFile(surveyId, sf.survey_answer_id, sf.file_id);
      setDeletedSurveyFileIds((prev) => new Set([...prev, sf.file_id]));
    },
    [surveyId]
  );

  const handleSurveyFileDetailBack = useCallback(() => {
    setSurveyFileDetail(null);
    setBackOverride(null);
  }, [setBackOverride]);

  const goToSurveyFileDetail = useCallback(
    (sf: ExistingSurveyFile) => {
      setSurveyFileDetail(sf);
      setStep('upload');
      setBackOverride(() => {
        setSurveyFileDetail(null);
        setBackOverride(null);
      });
    },
    [setBackOverride]
  );

  useEffect(() => {
    const totalCount = fileIds.length + visibleSurveyFiles.length;
    onHasUploadsChange?.(totalCount);
  }, [fileIds.length, visibleSurveyFiles.length, onHasUploadsChange]);

  return (
    <>
      <FileUploadInput
        onContinue={goToReview}
        onFileDetail={goToDetail}
        onHasUploadsChange={onHasUploadsChange}
        onReadyStateChange={onReadyStateChange}
        questionGroupId={questionGroupId}
        maxFiles={maxFiles}
        onAnswerGroupCreated={onAnswerGroupCreated}
        existingSurveyFiles={visibleSurveyFiles}
        onSurveyFileDetail={goToSurveyFileDetail}
        onSurveyFileDelete={handleSurveyFileDelete}
        onSurveyFilesLinked={handleSurveyFilesLinked}
        canUpload={canAnswer}
      />
      {step === 'detail' &&
        detailFileId !== null &&
        contentRef.current &&
        createPortal(
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            <FileDetail
              fileId={detailFileId}
              onBack={goToUpload}
              onRemove={handleRemoveFromDetail}
              onNotFound={goToUpload}
            />
          </Box>,
          contentRef.current
        )}
      {surveyFileDetail !== null &&
        contentRef.current &&
        createPortal(
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            <SurveyFileDetailView
              file={surveyFileDetail}
              onBack={handleSurveyFileDetailBack}
              onDelete={handleSurveyFileDelete}
            />
          </Box>,
          contentRef.current
        )}
      {step === 'review' &&
        contentRef.current &&
        createPortal(
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            <InsightsReview
              onComplete={handleCompleteWithFiles}
              onUploadMore={goToUpload}
            />
          </Box>,
          contentRef.current
        )}
    </>
  );
}
